import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { useSignOut } from "@/hooks/useSignOut";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, UserPlus, Shield, LogOut, Eye, EyeOff, Dumbbell, Calendar, Plus, Edit, GripVertical, BarChart3 } from "lucide-react";
import { supabase, supabaseAdmin } from "@/lib/supabase";
import BeautifulExerciseCalendar from "@/components/BeautifulExerciseCalendar";
import { UserProgressDashboard } from "@/components/UserProgressDashboard";
import ExerciseLibraryManager from "@/components/ExerciseLibraryManager";
import {
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface User {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  created_at: string;
  last_sign_in_at: string | null;
}

interface Exercise {
  id: string;
  name: string;
  description: string;
  created_by: string;
  created_at: string;
}

interface UserExercise {
  id: string;
  user_id: string;
  exercise_id: string;
  scheduled_date: string;
  sets: number;
  reps: number;
  weight: number | null;
  exercises: Exercise;
}

// Draggable Exercise Component
const DraggableExercise = ({ exercise }: { exercise: Exercise }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: exercise.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="p-3 border border-gray-300 rounded-lg cursor-grab hover:bg-primary/5 hover:border-primary/30 transition-all duration-200 group bg-white shadow-sm hover:shadow-md"
    >
      <div className="flex items-center gap-3">
        <GripVertical className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
        <div className="font-medium text-gray-800 group-hover:text-primary transition-colors">{exercise.name}</div>
      </div>
    </div>
  );
};

// Draggable Exercise Item Component (for exercises already in the program)
const DraggableExerciseItem = ({ 
  exercise, 
  dayDate, 
  exerciseIndex, 
  onEdit, 
  onRemove 
}: { 
  exercise: any; 
  dayDate: string; 
  exerciseIndex: number;
  onEdit: (dayDate: string, exerciseIndex: number) => void;
  onRemove: (dayDate: string, exerciseIndex: number) => void;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `exercise-${dayDate}-${exerciseIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative p-2 bg-gray-600 border border-gray-500 rounded-lg text-xs cursor-grab hover:bg-gray-500 transition-colors group shadow-sm"
      onClick={() => onEdit(dayDate, exerciseIndex)}
    >
      <div className="font-medium truncate pr-6 text-xs text-white leading-tight">{exercise.name}</div>
      <div className="text-gray-300 text-xs leading-tight">
        {exercise.sets}×{exercise.reps} {exercise.weight && `@ ${exercise.weight}kg`}
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-4 w-4 p-0 opacity-0 group-hover:opacity-100 transition-opacity absolute top-1 right-1 text-xs hover:bg-red-600 hover:text-white text-gray-400 rounded-full"
        onClick={(e) => {
          e.stopPropagation();
          onRemove(dayDate, exerciseIndex);
        }}
      >
        ×
      </Button>
    </div>
  );
};

// Droppable Day Component
const DroppableDay = ({ 
  day, 
  dayDate, 
  dayExercises, 
  isSelected, 
  isToday, 
  activeId, 
  onEdit, 
  onRemove, 
  onSetSelectedDay 
}: {
  day: string;
  dayDate: string;
  dayExercises: any[];
  isSelected: boolean;
  isToday: boolean;
  activeId: string | null;
  onEdit: (dayDate: string, exerciseIndex: number) => void;
  onRemove: (dayDate: string, exerciseIndex: number) => void;
  onSetSelectedDay: (dayDate: string) => void;
}) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `day-${dayDate}`,
  });

  const isDropTarget = activeId && activeId !== dayDate;
  const isDragging = activeId !== null;

  return (
    <div 
      ref={setNodeRef}
      className={`border-2 rounded-xl p-3 min-h-[160px] transition-all duration-300 bg-gray-800 flex flex-col cursor-pointer ${
        isSelected ? 'border-primary bg-primary/20 shadow-lg' : 
        isToday ? 'border-blue-400 bg-blue-900/30 shadow-md' : 
        isOver ? 'border-green-400 bg-green-900/40 shadow-xl scale-105 transform' :
        isDropTarget ? 'border-green-400 bg-green-900/30 shadow-lg' :
        isDragging ? 'border-yellow-400 bg-yellow-900/25 shadow-md hover:border-yellow-300' :
        'border-gray-600 hover:border-gray-500 hover:bg-gray-750'
      }`}
    >
      {/* Day Header */}
      <div className="text-center mb-2">
        <div className={`font-semibold text-sm ${isToday ? 'text-blue-400' : 'text-white'}`}>{day}</div>
        <div className="text-xs text-gray-300">
          {new Date(dayDate).getDate()}
        </div>
        {isDragging && !isOver && (
          <div className="text-xs text-yellow-400 mt-1 font-medium">
            Drop here
          </div>
        )}
      </div>
      
      {/* Exercises List */}
      <div className="flex-1 overflow-hidden">
        <SortableContext items={dayExercises.map((_, exIndex) => `exercise-${dayDate}-${exIndex}`)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1 mb-2 max-h-32 overflow-y-auto pr-1">
            {dayExercises.map((exercise, exIndex) => (
              <DraggableExerciseItem
                key={exIndex}
                exercise={exercise}
                dayDate={dayDate}
                exerciseIndex={exIndex}
                onEdit={onEdit}
                onRemove={onRemove}
              />
            ))}
          </div>
        </SortableContext>
      </div>
      
      {/* Drop Zone Indicator */}
      {isOver && (
        <div className="mb-2 p-3 border-2 border-dashed border-green-400 bg-green-900/40 rounded-lg text-center animate-pulse shadow-lg">
          <div className="text-xs text-green-400 font-bold">🎯 Drop here</div>
          <div className="text-xs text-green-300 mt-1">Add to {day}</div>
        </div>
      )}
      
      {/* Subtle drop indicator when dragging */}
      {isDragging && !isOver && dayExercises.length === 0 && (
        <div className="mb-2 p-2 border border-dashed border-yellow-400 bg-yellow-900/20 rounded text-center">
          <div className="text-xs text-yellow-400">Drop exercise here</div>
        </div>
      )}
      
      {/* Add Button - Only show when not dragging */}
      {!activeId && (
        <div className="mt-auto">
          <Button
            size="sm"
            variant={isSelected ? "default" : "outline"}
            className={`w-full h-6 text-xs ${
              isSelected 
                ? 'bg-primary hover:bg-primary/90 text-white' 
                : 'border-gray-500 bg-gray-600 hover:bg-gray-500 text-white opacity-60 hover:opacity-100'
            }`}
            onClick={() => onSetSelectedDay(dayDate)}
          >
            {isSelected ? '✓ Selected' : '+ Add Exercise'}
          </Button>
          <div className="text-xs text-gray-500 text-center mt-1">
            or drag from library
          </div>
        </div>
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfile();
  const { handleSignOut } = useSignOut();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Exercise management state
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loadingExercises, setLoadingExercises] = useState(true);
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [programData, setProgramData] = useState<{[key: string]: {exerciseId: string, sets: string, reps: string, weight: string}}>({});
  const [savingProgram, setSavingProgram] = useState(false);
  const [programSuccess, setProgramSuccess] = useState('');
  const [programError, setProgramError] = useState('');
  
  // Existing programs state
  const [existingPrograms, setExistingPrograms] = useState<UserExercise[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  
  // Quick add exercise state
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddData, setQuickAddData] = useState({
    exerciseId: '',
    date: '',
    sets: '',
    reps: '',
    weight: ''
  });
  const [addingExercise, setAddingExercise] = useState(false);
  const [quickAddError, setQuickAddError] = useState('');
  const [quickAddSuccess, setQuickAddSuccess] = useState('');
  const [showTemplates, setShowTemplates] = useState(false);
  const [selectedDay, setSelectedDay] = useState<string>('');
  const [dayPrograms, setDayPrograms] = useState<{[key: string]: any[]}>({});
  const [editingExercise, setEditingExercise] = useState<{day: string, index: number} | null>(null);
  const [exerciseEditData, setExerciseEditData] = useState({sets: '', reps: '', weight: ''});
  
  
  // Drag and drop state
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedExercise, setDraggedExercise] = useState<Exercise | null>(null);
  
  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );
  
  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    role: 'user' as 'user' | 'admin'
  });
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');

  useEffect(() => {
    // Redirect if not admin
    if (!profileLoading && (!profile || profile.role !== 'admin')) {
      navigate('/dashboard');
    }
  }, [profile, profileLoading, navigate]);

  useEffect(() => {
    if (profile?.role === 'admin') {
      loadUsers();
      loadExercises();
    }
  }, [profile]);

  useEffect(() => {
    if (selectedUser) {
      loadUserPrograms(selectedUser);
      // Set default week to current week start if not already set
      if (!selectedWeek) {
        setSelectedWeek(getCurrentWeekStart());
      }
    } else {
      setExistingPrograms([]);
    }
  }, [selectedUser]);

  useEffect(() => {
    // Set default week to current week start on component mount
    if (!selectedWeek) {
      setSelectedWeek(getCurrentWeekStart());
    }
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      // Get auth users using admin client
      const { data: authUsers, error: authError } = await supabaseAdmin.auth.admin.listUsers();
      
      if (authError) {
        console.error('Error loading auth users:', authError);
        return;
      }

      // Get profiles
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('*');

      if (profileError) {
        console.error('Error loading profiles:', profileError);
        return;
      }

      // Combine data
      const combinedUsers = authUsers.users.map(authUser => {
        const profile = profiles?.find(p => p.id === authUser.id);
        return {
          id: authUser.id,
          email: authUser.email || '',
          full_name: profile?.full_name || null,
          role: profile?.role || 'user',
          created_at: authUser.created_at,
          last_sign_in_at: authUser.last_sign_in_at
        };
      });

      setUsers(combinedUsers);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const createUser = async () => {
    if (!formData.email || !formData.password || !formData.fullName) {
      setFormError('All fields are required');
      return;
    }

    setCreating(true);
    setFormError('');
    setFormSuccess('');

    try {
      // Create auth user using admin API
      const { data: authUser, error: authError } = await supabaseAdmin.auth.admin.createUser({
        email: formData.email,
        password: formData.password,
        email_confirm: true // Auto-confirm email
      });

      if (authError) {
        setFormError(authError.message);
        return;
      }

      if (!authUser.user) {
        setFormError('Failed to create user');
        return;
      }

      // Create profile
      const { error: profileError } = await supabase
        .from('profiles')
        .insert({
          id: authUser.user.id,
          full_name: formData.fullName,
          role: formData.role
        });

      if (profileError) {
        setFormError('User created but profile failed: ' + profileError.message);
        return;
      }

      setFormSuccess(`User ${formData.email} created successfully!`);
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        fullName: '',
        role: 'user'
      });
      
      // Reload users list
      loadUsers();
      
      // Hide form after success
      setTimeout(() => {
        setShowCreateForm(false);
        setFormSuccess('');
      }, 2000);

    } catch (error) {
      console.error('Error creating user:', error);
      setFormError('Unexpected error occurred');
    } finally {
      setCreating(false);
    }
  };

  const loadExercises = async () => {
    setLoadingExercises(true);
    console.log('🏋️ Loading exercises...');
    try {
      const { data, error } = await supabase
        .from('exercises')
        .select('*')
        .order('name');

      if (error) {
        console.error('❌ Error loading exercises:', error);
      } else {
        console.log('✅ Exercises loaded:', data?.length || 0, 'exercises');
        setExercises(data || []);
      }
    } catch (error) {
      console.error('💥 Error loading exercises:', error);
    } finally {
      setLoadingExercises(false);
    }
  };

  const getWeekDates = (weekStart: string) => {
    const start = new Date(weekStart + 'T00:00:00');
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const getCurrentWeekStart = () => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const daysToMonday = currentDay === 0 ? -6 : 1 - currentDay; // If Sunday, go back 6 days, otherwise go to Monday
    const monday = new Date(today);
    monday.setDate(today.getDate() + daysToMonday);
    return monday.toISOString().split('T')[0];
  };

  const loadUserPrograms = async (userId: string) => {
    if (!userId) return;
    
    setLoadingPrograms(true);
    console.log('📅 Loading programs for user:', userId);
    
    try {
      // Load ALL programs for this user (not just future ones)
      const { data, error } = await supabase
        .from('user_exercises')
        .select(`
          *,
          exercises (
            id,
            name,
            description
          )
        `)
        .eq('user_id', userId)
        .order('scheduled_date', { ascending: true });

      if (error) {
        console.error('❌ Error loading user programs:', error);
        setExistingPrograms([]);
      } else {
        console.log('✅ User programs loaded:', data?.length || 0, 'exercises');
        console.log('📋 Program details:', data);
        setExistingPrograms(data || []);
      }
    } catch (error) {
      console.error('💥 Error loading user programs:', error);
    } finally {
      setLoadingPrograms(false);
    }
  };

  const quickAddExercise = async () => {
    if (!selectedUser || !quickAddData.exerciseId || !quickAddData.date || !quickAddData.sets || !quickAddData.reps) {
      setQuickAddError('Please fill in all required fields');
      return;
    }

    setAddingExercise(true);
    setQuickAddError('');
    setQuickAddSuccess('');

    try {
      const { error } = await supabase
        .from('user_exercises')
        .insert({
          user_id: selectedUser,
          exercise_id: quickAddData.exerciseId,
          scheduled_date: quickAddData.date,
          sets: parseInt(quickAddData.sets),
          reps: parseInt(quickAddData.reps),
          weight: quickAddData.weight ? parseFloat(quickAddData.weight) : null
        });

      if (error) {
        setQuickAddError('Error adding exercise: ' + error.message);
      } else {
        setQuickAddSuccess('Exercise added successfully!');
        setQuickAddData({
          exerciseId: '',
          date: '',
          sets: '',
          reps: '',
          weight: ''
        });
        setShowQuickAdd(false);
        // Reload user programs
        loadUserPrograms(selectedUser);
      }
    } catch (error) {
      console.error('Error adding exercise:', error);
      setQuickAddError('Unexpected error occurred');
    } finally {
      setAddingExercise(false);
    }
  };

  // Professional template functions
  const applyTemplate = (templateType: string) => {
    if (!selectedUser || !selectedWeek) {
      setProgramError('Please select a user and week first');
      return;
    }

    const weekDates = getWeekDates(selectedWeek);
    let templateData: any = {};

    switch (templateType) {
      case 'strength':
        // 5-day strength program
        templateData = {
          day1: { exerciseId: getExerciseId('Bench Press'), sets: '4', reps: '8', weight: '' },
          day2: { exerciseId: getExerciseId('Squats'), sets: '4', reps: '8', weight: '' },
          day3: { exerciseId: getExerciseId('Overhead Press'), sets: '4', reps: '8', weight: '' },
          day4: { exerciseId: getExerciseId('Deadlifts'), sets: '4', reps: '5', weight: '' },
          day5: { exerciseId: getExerciseId('Rows'), sets: '4', reps: '8', weight: '' }
        };
        break;
      case 'cardio':
        // 4-day cardio/HIIT program
        templateData = {
          day1: { exerciseId: getExerciseId('Burpees'), sets: '3', reps: '15', weight: '' },
          day2: { exerciseId: getExerciseId('Mountain Climbers'), sets: '3', reps: '20', weight: '' },
          day4: { exerciseId: getExerciseId('Jumping Jacks'), sets: '3', reps: '30', weight: '' },
          day6: { exerciseId: getExerciseId('Burpees'), sets: '3', reps: '15', weight: '' }
        };
        break;
      case 'fullbody':
        // 3-day full body program
        templateData = {
          day1: { exerciseId: getExerciseId('Push-ups'), sets: '3', reps: '12', weight: '' },
          day3: { exerciseId: getExerciseId('Squats'), sets: '3', reps: '15', weight: '' },
          day5: { exerciseId: getExerciseId('Plank'), sets: '3', reps: '30', weight: '' }
        };
        break;
      case 'beginner':
        // 3-day beginner program
        templateData = {
          day1: { exerciseId: getExerciseId('Push-ups'), sets: '2', reps: '8', weight: '' },
          day3: { exerciseId: getExerciseId('Squats'), sets: '2', reps: '10', weight: '' },
          day5: { exerciseId: getExerciseId('Plank'), sets: '2', reps: '20', weight: '' }
        };
        break;
      case 'weight_loss':
        // 6-day weight loss program
        templateData = {
          day1: { exerciseId: getExerciseId('Burpees'), sets: '3', reps: '12', weight: '' },
          day2: { exerciseId: getExerciseId('Mountain Climbers'), sets: '3', reps: '20', weight: '' },
          day3: { exerciseId: getExerciseId('Jumping Jacks'), sets: '3', reps: '25', weight: '' },
          day4: { exerciseId: getExerciseId('Push-ups'), sets: '3', reps: '10', weight: '' },
          day5: { exerciseId: getExerciseId('Squats'), sets: '3', reps: '15', weight: '' },
          day6: { exerciseId: getExerciseId('Plank'), sets: '3', reps: '30', weight: '' }
        };
        break;
    }

    setProgramData(templateData);
    setProgramSuccess(`Applied ${templateType} template! Review and create program.`);
  };

  const getExerciseId = (exerciseName: string) => {
    const exercise = exercises.find(ex => ex.name.toLowerCase().includes(exerciseName.toLowerCase()));
    return exercise?.id || '';
  };

  const duplicateLastWeek = () => {
    if (!selectedUser) {
      setProgramError('Please select a user first');
      return;
    }
    // This would load the previous week's program and apply it
    setProgramSuccess('Feature coming soon: Duplicate last week');
  };

  const clearAllPrograms = () => {
    if (!selectedUser) {
      setProgramError('Please select a user first');
      return;
    }
    setProgramData({});
    setProgramSuccess('Program cleared');
  };

  const exportProgram = () => {
    if (existingPrograms.length === 0) {
      setProgramError('No programs to export');
      return;
    }
    // This would export the program as PDF or CSV
    setProgramSuccess('Feature coming soon: Export program');
  };

  // Visual Program Builder Functions
  const addExerciseToDay = (exercise: any, dayDate?: string) => {
    const targetDay = dayDate || selectedDay;
    
    if (!targetDay) {
      setProgramError('Please select a day first by clicking "+ Add Exercise" on a day');
      return;
    }

    const newExercise = {
      id: exercise.id,
      name: exercise.name,
      sets: 3,
      reps: 10,
      weight: null
    };

    setDayPrograms(prev => ({
      ...prev,
      [targetDay]: [...(prev[targetDay] || []), newExercise]
    }));

    // Only clear selection if we're using the selectedDay (not drag & drop)
    if (!dayDate) {
      setSelectedDay('');
    }
    setProgramSuccess(`Added ${exercise.name} to ${new Date(targetDay).toLocaleDateString()}`);
  };

  const removeExerciseFromDay = (dayDate: string, exerciseIndex: number) => {
    setDayPrograms(prev => ({
      ...prev,
      [dayDate]: prev[dayDate]?.filter((_, index) => index !== exerciseIndex) || []
    }));
  };

  const getDayExercises = (dayDate: string) => {
    return dayPrograms[dayDate] || [];
  };

  const getTotalExercises = () => {
    return Object.values(dayPrograms).reduce((total, dayExercises) => total + dayExercises.length, 0);
  };

  const getActiveDays = () => {
    return Object.keys(dayPrograms).filter(day => dayPrograms[day].length > 0).length;
  };

  const editExercise = (dayDate: string, exerciseIndex: number) => {
    const exercise = dayPrograms[dayDate][exerciseIndex];
    setEditingExercise({day: dayDate, index: exerciseIndex});
    setExerciseEditData({
      sets: exercise.sets.toString(),
      reps: exercise.reps.toString(),
      weight: exercise.weight ? exercise.weight.toString() : ''
    });
  };

  const saveExerciseEdit = () => {
    if (!editingExercise) return;

    const {day, index} = editingExercise;
    setDayPrograms(prev => ({
      ...prev,
      [day]: prev[day].map((exercise, i) => 
        i === index 
          ? {
              ...exercise,
              sets: parseInt(exerciseEditData.sets) || 3,
              reps: parseInt(exerciseEditData.reps) || 10,
              weight: exerciseEditData.weight ? parseFloat(exerciseEditData.weight) : null
            }
          : exercise
      )
    }));

    setEditingExercise(null);
    setExerciseEditData({sets: '', reps: '', weight: ''});
  };

  const createVisualProgram = async () => {
    if (!selectedUser || !selectedWeek) {
      setProgramError('Please select a user and week first');
      return;
    }

    const programEntries: any[] = [];
    const weekDates = getWeekDates(selectedWeek);

    // Convert dayPrograms to database format
    Object.entries(dayPrograms).forEach(([dayDate, exercises]) => {
      exercises.forEach((exercise) => {
        programEntries.push({
          user_id: selectedUser,
          exercise_id: exercise.id,
          scheduled_date: dayDate,
          sets: exercise.sets,
          reps: exercise.reps,
          weight: exercise.weight
        });
      });
    });

    if (programEntries.length === 0) {
      setProgramError('Please add at least one exercise to create a program');
      return;
    }

    setSavingProgram(true);
    setProgramError('');
    setProgramSuccess('');

    try {
      const { error } = await supabase
        .from('user_exercises')
        .insert(programEntries);

      if (error) {
        setProgramError('Error creating program: ' + error.message);
      } else {
        setProgramSuccess(`Program created successfully! ${programEntries.length} exercises scheduled.`);
        setDayPrograms({}); // Clear the visual builder
        // Reload programs to show the newly created ones
        if (selectedUser) {
          loadUserPrograms(selectedUser);
        }
      }
    } catch (error) {
      console.error('Error creating program:', error);
      setProgramError('Unexpected error occurred');
    } finally {
      setSavingProgram(false);
    }
  };

  const createWeeklyProgram = async () => {
    if (!selectedUser || !selectedWeek) {
      setProgramError('Please select a user and week');
      return;
    }

    setSavingProgram(true);
    setProgramError('');
    setProgramSuccess('');

    try {
      const weekDates = getWeekDates(selectedWeek);
      const programEntries = [];

      // Create program entries for each day
      for (const [dayKey, exercise] of Object.entries(programData)) {
        if (exercise.exerciseId && exercise.sets && exercise.reps) {
          const dayIndex = parseInt(dayKey.replace('day', '')) - 1;
          const scheduledDate = weekDates[dayIndex];

          programEntries.push({
            user_id: selectedUser,
            exercise_id: exercise.exerciseId,
            scheduled_date: scheduledDate,
            sets: parseInt(exercise.sets),
            reps: parseInt(exercise.reps),
            weight: exercise.weight ? parseFloat(exercise.weight) : null
          });
        }
      }

      if (programEntries.length === 0) {
        setProgramError('Please add at least one exercise');
        return;
      }

      const { error } = await supabase
        .from('user_exercises')
        .insert(programEntries);

      if (error) {
        setProgramError('Error creating program: ' + error.message);
      } else {
        setProgramSuccess(`Program created successfully! ${programEntries.length} exercises scheduled.`);
        setProgramData({});
        // Don't clear selectedUser and selectedWeek so we can see the created programs
        // Reload the programs to show the newly created ones
        if (selectedUser) {
          loadUserPrograms(selectedUser);
        }
      }
    } catch (error) {
      console.error('Error creating program:', error);
      setProgramError('Unexpected error occurred');
    } finally {
      setSavingProgram(false);
    }
  };


  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    // Find the exercise being dragged
    const exercise = exercises.find(ex => ex.id === active.id);
    setDraggedExercise(exercise || null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    console.log('🎯 Drag ended - Active:', active.id, 'Over:', over?.id);
    
    setActiveId(null);
    setDraggedExercise(null);
    
    if (!over) {
      console.log('🎯 No drop target found');
      return;
    }
    
    // If dropping on a day zone
    if (over.id.toString().startsWith('day-')) {
      const dayDate = over.id.toString().replace('day-', '');
      const exercise = exercises.find(ex => ex.id === active.id);
      console.log('🎯 Dropping on day:', dayDate, 'Exercise:', exercise?.name);
      
      if (exercise) {
        addExerciseToDay(exercise, dayDate);
      }
    }
    
    // If dropping on an existing exercise (reordering within a day)
    if (over.id.toString().startsWith('exercise-')) {
      const [dayDate, exerciseIndex] = over.id.toString().replace('exercise-', '').split('-');
      
      // Check if we're dragging from the exercise library
      const exercise = exercises.find(ex => ex.id === active.id);
      if (exercise && dayDate) {
        // Remove from current day if it exists
        setDayPrograms(prev => {
          const newPrograms = { ...prev };
          Object.keys(newPrograms).forEach(day => {
            newPrograms[day] = newPrograms[day].filter(ex => ex.id !== active.id);
          });
          
          // Add to new day
          if (!newPrograms[dayDate]) {
            newPrograms[dayDate] = [];
          }
          
          const newExercise = {
            id: exercise.id,
            name: exercise.name,
            sets: 3,
            reps: 10,
            weight: null
          };
          
          newPrograms[dayDate].splice(parseInt(exerciseIndex), 0, newExercise);
          return newPrograms;
        });
      }
      
      // Check if we're reordering within the same day
      if (active.id.toString().startsWith('exercise-')) {
        const [activeDayDate, activeExerciseIndex] = active.id.toString().replace('exercise-', '').split('-');
        
        if (activeDayDate === dayDate && activeExerciseIndex !== exerciseIndex) {
          setDayPrograms(prev => {
            const newPrograms = { ...prev };
            const dayExercises = [...(newPrograms[dayDate] || [])];
            
            // Reorder exercises
            const [movedExercise] = dayExercises.splice(parseInt(activeExerciseIndex), 1);
            dayExercises.splice(parseInt(exerciseIndex), 0, movedExercise);
            
            newPrograms[dayDate] = dayExercises;
            return newPrograms;
          });
        }
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    if (!over) return;
    
    // Add visual feedback for drop zones
    if (over.id.toString().startsWith('day-')) {
      // Day is a valid drop zone
    }
    
    if (over.id.toString().startsWith('exercise-')) {
      // Exercise is a valid drop zone for reordering
    }
  };

  // Show loading while checking admin status
  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Don't render if not admin (will redirect)
  if (!profile || profile.role !== 'admin') {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="font-black text-2xl gradient-text">AN</div>
              <Badge variant="secondary" className="bg-red-100 text-red-800">
                <Shield className="h-3 w-3 mr-1" />
                Admin Dashboard
              </Badge>
            </div>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Welcome */}
        <div className="mb-4 sm:mb-6">
          <h1 className="text-xl sm:text-2xl font-bold gradient-text">Admin Dashboard</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Manage users, exercises, and weekly programs</p>
        </div>

        <Tabs defaultValue="users" className="space-y-3 sm:space-y-6">
          <TabsList className="grid w-full grid-cols-3 h-7 sm:h-10">
            <TabsTrigger value="users" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm py-0.5 sm:py-1">
              <Users className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">User Management</span>
              <span className="sm:hidden">Users</span>
            </TabsTrigger>
            <TabsTrigger value="programs" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm py-0.5 sm:py-1">
              <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Weekly Programs</span>
              <span className="sm:hidden">Programs</span>
            </TabsTrigger>
            <TabsTrigger value="exercises" className="flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm py-0.5 sm:py-1">
              <Dumbbell className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">Exercise Library</span>
              <span className="sm:hidden">Exercises</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4 sm:space-y-6">

        {/* Stats - Production Ready */}
        <div className="bg-muted/30 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <div className="text-lg font-bold text-foreground">{users.length}</div>
                  <div className="text-sm text-muted-foreground">Total Users</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">
                  {users.filter(u => u.role === 'admin').length} admin, {users.filter(u => u.role === 'user').length} users
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <div className="text-lg font-bold text-foreground">{users.filter(u => u.last_sign_in_at).length}</div>
                  <div className="text-sm text-muted-foreground">Active Users</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Logged in</div>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center">
                  <UserPlus className="h-5 w-5 text-orange-500" />
                </div>
                <div>
                  <div className="text-lg font-bold text-foreground">
                    {users.filter(u => {
                      const weekAgo = new Date();
                      weekAgo.setDate(weekAgo.getDate() - 7);
                      return new Date(u.created_at) > weekAgo;
                    }).length}
                  </div>
                  <div className="text-sm text-muted-foreground">New This Week</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-muted-foreground">Last 7 days</div>
              </div>
            </div>
          </div>
        </div>

        {/* Create User Section */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Create New User</CardTitle>
                <CardDescription>
                  Add new users to the system (only admins can create accounts)
                </CardDescription>
              </div>
              <Button 
                onClick={() => setShowCreateForm(!showCreateForm)}
                variant={showCreateForm ? "outline" : "default"}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {showCreateForm ? 'Cancel' : 'Create User'}
              </Button>
            </div>
          </CardHeader>

          {showCreateForm && (
            <CardContent>
              <Separator className="mb-4" />
              
              {formError && (
                <Alert className="mb-4 border-red-200 bg-red-50">
                  <AlertDescription className="text-red-800">{formError}</AlertDescription>
                </Alert>
              )}

              {formSuccess && (
                <Alert className="mb-4 border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800">{formSuccess}</AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="user@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    disabled={creating}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    placeholder="John Doe"
                    value={formData.fullName}
                    onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                    disabled={creating}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Secure password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      disabled={creating}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={creating}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({...formData, role: value as 'user' | 'admin'})}
                    disabled={creating}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-4">
                <Button 
                  onClick={createUser}
                  disabled={creating || !formData.email || !formData.password || !formData.fullName}
                  className="w-full md:w-auto"
                >
                  {creating ? 'Creating...' : 'Create User'}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Users List */}
        <Card>
          <CardHeader>
            <CardTitle>All Users</CardTitle>
            <CardDescription>
              Manage existing users in the system
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loadingUsers ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                <span className="ml-2">Loading users...</span>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-4">
                {users.map((user) => (
                  <div key={user.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 sm:p-4 border rounded-lg gap-2 sm:gap-0">
                    <div className="flex-1">
                      <div className="font-medium text-sm sm:text-base">{user.full_name || 'No name'}</div>
                      <div className="text-xs sm:text-sm text-muted-foreground">{user.email}</div>
                      <div className="text-xs text-muted-foreground mt-0.5 sm:mt-1">
                        <div className="sm:hidden">
                          Created: {new Date(user.created_at).toLocaleDateString()}
                        </div>
                        <div className="sm:hidden">
                          Last login: {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                        </div>
                        <div className="hidden sm:block">
                          Created: {new Date(user.created_at).toLocaleDateString()} • 
                          Last login: {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end space-x-1 sm:space-x-2">
                      <Badge 
                        variant={user.role === 'admin' ? 'destructive' : 'secondary'}
                        className="text-xs px-1.5 py-0.5"
                      >
                        {user.role}
                      </Badge>
                      <Badge variant={user.last_sign_in_at ? 'default' : 'outline'} className="text-xs px-1.5 py-0.5">
                        {user.last_sign_in_at ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

          </TabsContent>

          {/* Weekly Programs Tab */}
          <TabsContent value="programs" className="space-y-6">
            {/* Step 1: User Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Step 1: Select User
                </CardTitle>
                <CardDescription>
                  Choose a user to manage their exercise programs
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Select User</Label>
                    <Select value={selectedUser} onValueChange={setSelectedUser}>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose a user to start" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter(u => u.role === 'user').map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.full_name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Progress Dashboard Button */}
                  {selectedUser && (
                    <div className="pt-4">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            className="flex items-center gap-2"
                          >
                            <BarChart3 className="h-4 w-4" />
                            View User Progress
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[95vh] sm:max-h-[90vh] bg-gradient-to-br from-background via-background to-muted/20 border-border mx-2 sm:mx-4">
                          <DialogHeader className="pb-3 sm:pb-4 border-b border-border">
                            <DialogTitle className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 text-lg sm:text-xl">
                              <div className="flex items-center gap-2 sm:gap-3">
                                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                                  <BarChart3 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                                </div>
                                <span className="gradient-text text-sm sm:text-base">User Progress Dashboard</span>
                              </div>
                              <Badge className="bg-purple-600 text-white text-xs px-2 py-1 self-start sm:self-center sm:ml-auto">
                                <Users className="h-3 w-3 mr-1" />
                                Admin Only
                              </Badge>
                            </DialogTitle>
                          </DialogHeader>
                          <div className="mt-3 sm:mt-6 overflow-y-auto max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-120px)]">
                            <UserProgressDashboard userId={selectedUser} />
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}

                </div>
                
              </CardContent>
            </Card>

            {/* Beautiful Exercise Calendar */}
                {selectedUser && (
              <BeautifulExerciseCalendar
                selectedUser={selectedUser}
                users={users}
                exercises={exercises}
                onExerciseUpdated={() => {
                  // Reload programs after exercise update
                  loadUserPrograms(selectedUser);
                }}
              />
            )}


            {/* Show message if no user selected */}
            {!selectedUser && (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-semibold mb-2">Select a User to Continue</h3>
                    <p className="text-muted-foreground">
                      Choose a user from the dropdown above to view and manage their exercise programs.
                    </p>
                        </div>
                </CardContent>
              </Card>
            )}

            {/* Messages */}
            {programError && (
              <Alert className="border-red-200 bg-red-50">
                <AlertDescription className="text-red-800">{programError}</AlertDescription>
                          </Alert>
                        )}

            {programSuccess && (
              <Alert className="border-green-200 bg-green-50">
                <AlertDescription className="text-green-800">{programSuccess}</AlertDescription>
                          </Alert>
                        )}







            {/* Edit Exercise Modal */}
            {editingExercise && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="w-96">
                  <CardHeader>
                    <CardTitle>Edit Exercise</CardTitle>
                    <CardDescription>
                      Adjust sets, reps, and weight for this exercise
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Sets</Label>
                        <Input
                          type="number"
                          value={exerciseEditData.sets}
                          onChange={(e) => setExerciseEditData(prev => ({...prev, sets: e.target.value}))}
                          placeholder="3"
                        />
                      </div>
                      <div>
                        <Label>Reps</Label>
                        <Input
                          type="number"
                          value={exerciseEditData.reps}
                          onChange={(e) => setExerciseEditData(prev => ({...prev, reps: e.target.value}))}
                          placeholder="10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Weight (kg) <span className="text-xs text-muted-foreground">(optional)</span></Label>
                      <Input
                        type="number"
                        step="0.5"
                        value={exerciseEditData.weight}
                        onChange={(e) => setExerciseEditData(prev => ({...prev, weight: e.target.value}))}
                        placeholder="0"
                      />
                    </div>
                    <div className="flex space-x-2">
                      <Button onClick={saveExerciseEdit} className="flex-1">
                        Save Changes
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => setEditingExercise(null)}
                        className="flex-1"
                      >
                        Cancel
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Exercise Library Tab */}
          <TabsContent value="exercises" className="space-y-3 sm:space-y-6">
            <ExerciseLibraryManager onExerciseUpdated={loadExercises} />
          </TabsContent>

        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
